import { ReactElement, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { queryKapProgramComment, updateComment } from 'src/screens/kap/program/details/summary/programSummaryQueries'
import { SummaryCommentEditForm, SummaryCommentFormValues } from 'src/screens/shared/common/SummaryCommentEditForm'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

type originType = 'SAVE' | 'SAVE_AND_BACK'

export const ProgramSummaryCommentEditPage = (): ReactElement => {
  const { programId } = useParams()
  const id = parseInt(programId as string)
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const originRef = useRef<originType>('SAVE')
  const notificationService = useNotificationService()
  const [commentData, setCommentData] = useState<string | null>(null)
  const { canEdit, loading } = usePermissionsForKapProgram(id)

  useEffect(() => {
    const initData = async () => {
      const { data, error } = await urqlClient
        .query<
          {
            kap_program_by_pk: Query_Root['kap_program_by_pk']
          },
          { programId: number }
        >(queryKapProgramComment, { programId: id })
        .toPromise()

      if (data?.kap_program_by_pk) {
        const comment = data.kap_program_by_pk.concept_comment ?? null
        setCommentData(comment)
      } else if (error) {
        notificationService.operationFailed()
      }
    }
    initData()
  }, [notificationService, id, urqlClient])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const onUpdateHandler = async (values: SummaryCommentFormValues) => {
    const updateObject = {
      programId: id,
      comment: HtmlSanitizer.sanitize(values.comment ?? ''),
    }
    const { error, data } = await urqlClient
      .mutation<{
        update_kap_program: Mutation_Root['update_kap_program']
      }>(updateComment, updateObject)
      .toPromise()
    const affectedRows = data?.update_kap_program?.affected_rows
    if (error || affectedRows !== 1) {
      notificationService.operationFailed()
    } else {
      setCommentData(values.comment)
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        navigate(ROUTES.KapDetailsRoot.nested.Summary.params({ programId }))
      }
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout
      title={getMessage('label.title.edit.comments')}
      onBack={() => {
        navigate(ROUTES.KapDetailsRoot.nested.Summary.params({ programId }))
      }}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && (
            <>
              <HelpAndInstructions labelKey="label.help.kap.program.summary.comments" defaultExpansion />
              <SummaryCommentEditForm comment={commentData} onSave={onUpdateHandler} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
